import { useRef, useState } from 'react';

interface Props {
  /** Emits the full code, e.g. "YOYO-AAAA-BBBB-CCCC" (segments may be partial). */
  onChange: (code: string) => void;
  disabled?: boolean;
}

// Codes are uppercase letters/digits in groups of 4 (the "YOYO-" prefix is fixed).
const sanitize = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Three 4-character inputs for entering a gift card code in the
 * YOYO-XXXX-XXXX-XXXX format. Auto-advances when a group fills, supports
 * pasting a full code into any box, and backspaces to the previous box.
 */
export default function GiftCardCodeInput({ onChange, disabled }: Props) {
  const [segs, setSegs] = useState<string[]>(['', '', '']);
  const ref0 = useRef<HTMLInputElement>(null);
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const refs = [ref0, ref1, ref2];

  const emit = (next: string[]) => onChange(`YOYO-${next[0]}-${next[1]}-${next[2]}`);

  const setSeg = (i: number, raw: string) => {
    // Strip a pasted "YOYO-" prefix, then remove separators.
    const cleaned = sanitize(raw.replace(/^YOYO-?/i, ''));

    // Pasting more than one group: distribute across segments from i onward.
    if (cleaned.length > 4) {
      const next = [...segs];
      let buf = cleaned.slice(0, 12 - i * 4);
      let last = i;
      for (let j = i; j < 3 && buf.length; j++) {
        next[j] = buf.slice(0, 4);
        buf = buf.slice(4);
        last = j;
      }
      setSegs(next);
      emit(next);
      refs[last].current?.focus();
      return;
    }

    const next = [...segs];
    next[i] = cleaned.slice(0, 4);
    setSegs(next);
    emit(next);
    if (cleaned.length >= 4 && i < 2) refs[i + 1].current?.focus();
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !segs[i] && i > 0) refs[i - 1].current?.focus();
  };

  // Browsers apply maxLength=4 before onChange fires, which would clip a pasted
  // full code (YOYO-AAAA-BBBB-CCCC) to just the first 4 chars. Intercept the
  // paste so setSeg sees the whole string and can distribute across segments.
  // A full code always populates from segment 0 regardless of the focused box.
  const onPaste = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    const cleaned = sanitize(text.replace(/^YOYO-?/i, ''));
    if (cleaned.length > 4) {
      e.preventDefault();
      const fullCode = /YOYO/i.test(text) || cleaned.length >= 12;
      setSeg(fullCode ? 0 : i, text);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-gray-500 select-none">YOYO-</span>
      {[0, 1, 2].map((i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-gray-400 select-none">-</span>}
          <input
            ref={refs[i]}
            type="text"
            inputMode="text"
            value={segs[i]}
            maxLength={4}
            disabled={disabled}
            onChange={(e) => setSeg(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
            placeholder="XXXX"
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg font-mono uppercase text-center tracking-widest focus:ring-primary focus:border-primary"
          />
        </span>
      ))}
    </div>
  );
}
